import React, { useEffect } from "react";
import { GetServerSideProps } from "next";
import { Block } from "../types/Block";
import { Meta } from "../types/Meta";
import SEO from "../src/components/seo";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Layout from "../src/components/layout";
import { MenuRoute } from "../types/MenuRoute";
import { City } from "../types/City";
import { getBaseDataForPage } from "../utils/getBaseDataForPage";
import { TopBar } from "../types/TopBar";
import HttpClient from "../utils/HttpClient";
import { Slug as SlugType } from "../types/Slug";
import DynamicPageBuilder from "../src/components/DynamicPageBuilder";
import { useCitiesState } from "../utils/CitiesContext";
import { useRouter } from "next/router";
import { optimizeActivateEvent } from "../utils/gtmEvents";
import { CompanyData } from "../types/CompanyData";

type SlugsProps = {
  blocks: Block[];
  meta: Meta;
  slug: string[];
  routes: MenuRoute[];
  cities: City[];
  topBars: TopBar[];
  slugs: SlugType[];
  canonicalUrl: string;
  companyData: CompanyData;
  hostUrl: string;
};

const Slug = (data: SlugsProps): React.ReactElement => {
  const { meta, blocks, cities, canonicalUrl } = data;
  const { isReady, asPath } = useRouter();
  const { update } = useCitiesState();
  useEffect(() => {
    update(cities);
  }, [cities]);

  useEffect(() => {
    if (isReady) {
      optimizeActivateEvent();
    }
  }, [asPath]);

  return (
    <>
      <SEO
        description={meta.description}
        title={meta.title}
        picture={meta.picture}
        canonicalUrl={canonicalUrl}
      />
      <DynamicPageBuilder blocks={blocks} />
    </>
  );
};

Slug.getLayout = function getLayout(
  data: SlugsProps,
  component: React.ReactElement
) {
  const { routes, topBars, slugs, companyData, hostUrl } = data;
  return (
    <Layout
      routes={routes}
      topBars={topBars}
      slugs={slugs}
      companyData={companyData}
      hostUrl={hostUrl}
    >
      {component}
    </Layout>
  );
};

export default Slug;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const {
    query: { slug },
    locale,
    resolvedUrl,
    res,
    req,
  } = context;

  if (process.env.NEXT_PUBLIC_CACHE_CONFIG) {
    res.setHeader("Cache-Control", process.env.NEXT_PUBLIC_CACHE_CONFIG);
  }

  let props = {};

  if (locale) {
    const { host: hostUrl } = req.headers;
    const index = resolvedUrl.indexOf("?");
    const url = index !== -1 ? resolvedUrl.substring(0, index) : resolvedUrl;
    const httpClient = new HttpClient(locale);
    const data = await getBaseDataForPage(httpClient, url);
    const protocol = req.headers["X-Forwarded-Proto"] ?? "https";
    const slugForCanonical = slug ? slug.toString().split(",").join("/") : "";
    const canonicalUrl = `${protocol}://${hostUrl}/${locale}/${slugForCanonical}`;

    props = {
      slug,
      hostUrl,
      canonicalUrl,
      ...data,
      ...props,
      ...(await serverSideTranslations(locale, ["common"])),
    };

    if (!data.blocks || data.blocks.length === 0) {
      return {
        redirect: {
          destination: "/404",
          permanent: false,
        },
      };
    }
  }

  return { props };
};
